import { Component, OnInit } from '@angular/core';
import { Edit, Eye, LucideAngularModule, Plus, Trash2 } from 'lucide-angular';
import { RouterLink } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventTemplateService } from '../shared/event-template.service';
import { EventTemplateModel, EventTemplateModelUtils } from '../shared/event-template.model';
import { NotificationService } from '@app/shared/notification/notification.service';
import { AuthService } from '@app/auth/auth.service';
import { UserModel } from '@app/users/shared/user.model';

@Component({
  selector: 'app-event-templates-index',
  imports: [DataTableComponent, LucideAngularModule, RouterLink],
  templateUrl: './event-templates-index.component.html',
  standalone: true
})
export class EventTemplatesIndexComponent extends Destroyer implements OnInit {
  constructor(
    private service: EventTemplateService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {
    super();
  }

  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly EyeIcon = Eye;
  protected readonly Trash2Icon = Trash2;

  protected templatesPage?: PagedEntities<EventTemplateModel>;
  protected currentUser: UserModel | null = null;
  protected isAdmin = false;

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 100,
  });

  searchControl = new FormControl('');

  tableColumns: Column<EventTemplateModel>[] = [
    { field: 'name', header: 'Template Name', sortable: true, width: '20%' },
    {
      field: 'event_type',
      header: 'Event Type',
      type: 'badge',
      sortable: true,
      width: '12%',
      getColor: (item: EventTemplateModel) => EventTemplateModelUtils.getEventTypeColor(item.event_type),
    },
    {
      field: 'venue',
      header: 'Venue',
      width: '15%',
      callback: (item: EventTemplateModel) => item.venue?.name || 'All Venues',
    },
    {
      field: 'items_count',
      header: 'Timeline Items',
      width: '8%',
      callback: (item: EventTemplateModel) => item.items_count?.toString() || '0',
    },
    {
      field: 'events_count',
      header: 'Used in Events',
      width: '8%',
      callback: (item: EventTemplateModel) => item.events_count?.toString() || '0',
    },
    {
      field: 'creator',
      header: 'Created By',
      width: '8%',
      callback: (item: EventTemplateModel) => item.creator?.name || '',
    },
    { field: 'created_at', type: 'date', header: 'Created', sortable: true, width: '9%' },
  ];

  rowActions: RowAction<EventTemplateModel>[] = [
    {
      icon: this.EyeIcon,
      label: 'View',
      routerLink: (item: EventTemplateModel) => ['/event-templates', item.id!.toString()],
    },
    {
      icon: this.EditIcon,
      label: 'Edit',
      routerLink: (item: EventTemplateModel) => ['/event-templates', item.id!.toString(), 'edit'],
      condition: () => this.isAdmin,
    },
    {
      icon: this.Trash2Icon,
      label: 'Delete',
      callback: (item: EventTemplateModel) => this.deleteTemplate(item),
      requireConfirm: true,
      confirmMessage: (item: EventTemplateModel) =>
        `Are you sure you want to delete the template "${item.name}"? This action cannot be undone.`,
      condition: () => this.isAdmin,
    },
  ];

  ngOnInit(): void {
    this.authService.getUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.loadData();
      },
      error: () => {
        this.isAdmin = false;
        this.loadData();
      },
    });
    this.setupSearch();
  }

  private loadData(): void {
    this.service.getPage(this.routeParams).subscribe({
      next: (page) => {
        this.templatesPage = page;
      },
      error: (error) => {
        this.notificationService.error('Failed to load templates');
        console.error('Error loading templates:', error);
      }
    });
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.routeParams.filter = [{ field: 'name', value: searchTerm }];
        this.routeParams.pageNum = 1;
        this.loadData();
      });
  }

  handleRoute(routeParams: RouteParams): void {
    this.routeParams = routeParams;
    this.loadData();
  }

  private deleteTemplate(template: EventTemplateModel): void {
    if (!this.isAdmin || !template.id) {
      return;
    }

    this.service.delete(template.id.toString()).subscribe({
      next: () => {
        this.notificationService.success(`Template "${template.name}" has been deleted`);
        this.loadData(); // Refresh the list
      },
      error: (error) => {
        this.notificationService.error('Failed to delete template');
        console.error('Error deleting template:', error);
      }
    });
  }
}
